import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { motion } from 'motion/react';
import { useQuizletAnalytics, useWidgetPreferences, useSaveWidgetPreferences } from '@/queries/analytics';
import { useQuizlet } from '@/queries/quizlets';
import { DashboardFilters } from '@/components/analytics/DashboardFilters';
import { AnalyticsDashboard } from '@/components/analytics/AnalyticsDashboard';
import { WidgetCustomizer } from '@/components/analytics/WidgetCustomizer';
import { CircularLoader } from '@/components/CircularLoader';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/queries/query-keys';
import type { AnalyticsFilters, TimeRange, WidgetId } from '@/types/analytics';
import styles from './quizletId.module.scss';

export const Route = createFileRoute('/analytics/$quizletId')({
  beforeLoad: async ({ context }: { context: any }) => {
    if (context.authLoading) return;
    const user = context.user;
    if (!user) {
      throw redirect({ to: '/login' });
    }

    const queryClient = context.queryClient;

    // Check admin status
    try {
      const data = await queryClient.fetchQuery({
        queryKey: queryKeys.admin.status(),
        queryFn: async () => {
          const { data, error } = await supabase
            .from('user_profiles')
            .select('permission_level')
            .eq('user_id', user.id)
            .single();

          if (error) throw error;

          return {
            isAdmin: data?.permission_level?.admin === true,
          };
        },
        staleTime: 5 * 60 * 1000,
      });

      if (!data.isAdmin) {
        throw redirect({ to: '/analytics' });
      }
    } catch (error) {
      if (error instanceof Response || (error as any)?.isRedirect) {
        throw error;
      }
      throw redirect({ to: '/analytics' });
    }
  },
  component: QuizletAnalyticsDashboard,
});

function QuizletAnalyticsDashboard() {
  const { quizletId } = Route.useParams();

  // Initialize filters with default values
  const [filters, setFilters] = useState<AnalyticsFilters>({
    timeRange: 'last30' as TimeRange,
    isDailyKnowledge: false,
  });

  const [isCustomizerOpen, setIsCustomizerOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Fetch quizlet basic info
  const { data: quizlet, isLoading: quizletLoading } = useQuizlet(quizletId);

  // Fetch quizlet analytics data
  const {
    data: analyticsData,
    isLoading: analyticsLoading,
    error,
  } = useQuizletAnalytics(quizletId, filters);

  // Fetch widget preferences
  const { data: preferences } = useWidgetPreferences();
  const savePreferences = useSaveWidgetPreferences();

  const isLoading = quizletLoading || analyticsLoading;

  // Get quizlet dashboard preferences
  const quizletPrefs = preferences?.find((p) => p.dashboardType === 'quizlet');
  const enabledWidgets = quizletPrefs?.enabledWidgets || [
    'overall-success-rate',
    'performance-by-difficulty',
    'performance-by-type',
    'improvement-trends',
    'most-challenging',
    'questions-mastered',
  ];
  const widgetOrder = quizletPrefs?.widgetOrder || enabledWidgets;

  // Handle filter changes
  const handleFiltersChange = (newFilters: Partial<AnalyticsFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const handleWidgetOrderChange = (newOrder: WidgetId[]) => {
    savePreferences.mutate({
      dashboardType: 'quizlet',
      enabledWidgets,
      widgetOrder: newOrder,
      widgetSettings: quizletPrefs?.widgetSettings || ({} as Record<WidgetId, any>),
    });
  };

  const handleSaveWidgets = (selectedWidgets: WidgetId[]) => {
    savePreferences.mutate({
      dashboardType: 'quizlet',
      enabledWidgets: selectedWidgets,
      widgetOrder: selectedWidgets,
      widgetSettings: quizletPrefs?.widgetSettings || ({} as Record<WidgetId, any>),
    });
  };

  if (isLoading) {
    return (
      <div className={styles.quizletAnalytics__loadingContainer}>
        <CircularLoader />
        <p className={styles.quizletAnalytics__loadingText}>
          Loading analytics data...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.quizletAnalytics__errorContainer}>
        <div className={styles.quizletAnalytics__errorContent}>
          <h2 className={styles.quizletAnalytics__errorTitle}>
            Error Loading Analytics
          </h2>
          <p className={styles.quizletAnalytics__errorMessage}>{error.message}</p>
          <Link to="/analytics">
            <motion.button
              className={styles.quizletAnalytics__backButton}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Back to Global Analytics
            </motion.button>
          </Link>
        </div>
      </div>
    );
  }

  if (!quizlet) {
    return (
      <div className={styles.quizletAnalytics__notFoundContainer}>
        <p className={styles.quizletAnalytics__notFoundText}>Quizlet not found</p>
        <Link to="/analytics">
          <motion.button
            className={styles.quizletAnalytics__backButton}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Back to Global Analytics
          </motion.button>
        </Link>
      </div>
    );
  }

  return (
    <div className={styles.quizletAnalytics}>
      <div className={styles.quizletAnalytics__header}>
        <div className={styles.quizletAnalytics__headerContent}>
          <div>
            <div className={styles.quizletAnalytics__breadcrumb}>
              <Link
                to="/analytics"
                className={styles.quizletAnalytics__breadcrumbLink}
              >
                Analytics
              </Link>
              <span className={styles.quizletAnalytics__breadcrumbSeparator}>
                /
              </span>
              <span className={styles.quizletAnalytics__breadcrumbCurrent}>
                {quizlet.name}
              </span>
            </div>
            <h1 className={styles.quizletAnalytics__title}>{quizlet.name}</h1>
            <p className={styles.quizletAnalytics__subtitle}>
              Detailed analytics for this quizlet
            </p>
          </div>
          <div className={styles.quizletAnalytics__headerActions}>
            <motion.button
              className={styles.quizletAnalytics__customizeButton}
              onClick={() => setIsEditMode(!isEditMode)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                backgroundColor: isEditMode ? 'var(--primary-color, #0f766e)' : 'transparent',
                color: isEditMode ? 'white' : 'var(--text-primary)',
                border: isEditMode ? '1px solid var(--primary-color, #0f766e)' : '1px solid var(--border-color, #e5e5e5)',
              }}
            >
              {isEditMode ? 'Done Organizing' : 'Rearrange Layout'}
            </motion.button>
            <motion.button
              className={styles.quizletAnalytics__customizeButton}
              onClick={() => setIsCustomizerOpen(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Customize Widgets
            </motion.button>
            <Link
              to="/quizlets/$quizletId"
              params={{ quizletId }}
            >
              <motion.button
                className={styles.quizletAnalytics__practiceButton}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Practice Quiz
              </motion.button>
            </Link>
          </div>
        </div>
      </div>

      <DashboardFilters
        filters={filters}
        onChange={handleFiltersChange}
        showDailyKnowledgeToggle={false}
      />

      <div className={styles.quizletAnalytics__content}>
        {analyticsData ? (
          <AnalyticsDashboard
            data={analyticsData as any}
            isLoading={analyticsLoading}
            error={error}
            enabledWidgets={enabledWidgets}
            widgetOrder={widgetOrder}
            onWidgetOrderChange={handleWidgetOrderChange}
            isEditMode={isEditMode}
          />
        ) : (
          <div className={styles.quizletAnalytics__noDataContainer}>
            <p className={styles.quizletAnalytics__noDataText}>
              No analytics data available yet. Start practicing this quizlet to see
              your progress!
            </p>
          </div>
        )}
      </div>

      <WidgetCustomizer
        isOpen={isCustomizerOpen}
        onClose={() => setIsCustomizerOpen(false)}
        enabledWidgets={enabledWidgets}
        onSave={handleSaveWidgets}
      />
    </div>
  );
}
